import { useState, useEffect, type KeyboardEvent } from 'react'
import { format } from 'date-fns'
import { Copy, Archive, X, HelpCircle, Tag } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { BucketColorPicker } from '@/components/buckets/BucketColorPicker'
import { PriorityStatusBadge, STATUS_CONFIG } from './PriorityStatusBadge'
import { ReleaseDialog } from './ReleaseDialog'
import {
  usePriority, usePriorityHistory,
  useUpdatePriority, useSetPriorityStatus, useArchivePriority, useDuplicatePriority,
} from '@/hooks/usePriorities'
import { useCreateAndAssignTag, useAddTagToPriority, useRemoveTagFromPriority } from '@/hooks/useValueTags'
import { useNotesByPriority } from '@/hooks/useNotes'
import { useEntriesForPriority } from '@/hooks/useJournal'
import { useBuckets } from '@/hooks/useBuckets'
import { useValueTags, useTagsForPriority } from '@/hooks/useValueTags'
import { NoteThread } from '@/components/notes/NoteThread'
import { cn } from '@/lib/utils'
import type { Priority, PriorityStatus, PriorityOrigin, PriorityEnergy, PriorityHorizon, PriorityCadence } from '@/types'

// ─── Inline helpers ───────────────────────────────────────────────────────────

function SectionLabel({
  children,
  description,
}: {
  children: React.ReactNode
  description?: string
}) {
  return (
    <div className="mb-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {children}
      </p>
      {description && (
        <p className="text-xs text-muted-foreground/70 mt-1 leading-relaxed">{description}</p>
      )}
    </div>
  )
}

function FieldRow({
  label,
  tooltip,
  children,
}: {
  label: string
  tooltip?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <div className="flex items-center gap-1 w-28 flex-shrink-0 pt-0.5">
        <span className="text-xs text-muted-foreground">{label}</span>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground/40 hover:text-muted-foreground transition-colors focus-visible:outline-none focus-visible:text-muted-foreground"
                aria-label={`Help: ${label}`}
              >
                <HelpCircle size={10} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-52 text-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}

function ToggleGroup<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T | undefined
  options: { value: T; label: string }[]
  onChange: (v: T | undefined) => void
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(value === opt.value ? undefined : opt.value)}
          className={cn(
            'px-2.5 py-1 rounded text-xs border transition-colors',
            value === opt.value
              ? 'bg-foreground text-background border-foreground'
              : 'bg-background text-muted-foreground border-border hover:border-foreground/40',
          )}
          aria-pressed={value === opt.value}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function DotScale({
  value,
  onChange,
  max = 5,
  'aria-label': ariaLabel,
}: {
  value: number | undefined
  onChange: (v: number | undefined) => void
  max?: number
  'aria-label': string
}) {
  return (
    <div className="flex gap-1.5" role="group" aria-label={ariaLabel}>
      {Array.from({ length: max }).map((_, i) => {
        const level = i + 1
        const filled = value !== undefined && level <= value
        return (
          <button
            key={i}
            type="button"
            onClick={() => onChange(value === level ? undefined : level)}
            className={cn(
              'w-5 h-5 rounded-full border-2 transition-colors',
              filled
                ? 'bg-foreground border-foreground'
                : 'bg-background border-border hover:border-foreground/40',
            )}
            aria-label={`${level} of ${max}`}
            aria-pressed={value === level}
          />
        )
      })}
    </div>
  )
}

// ─── Tag assigner ─────────────────────────────────────────────────────────────

function TagAssigner({ priorityId }: { priorityId: string }) {
  const allTags = useValueTags()
  const assignedTags = useTagsForPriority(priorityId)
  const [inputValue, setInputValue] = useState('')
  const [showAll, setShowAll] = useState(false)
  const addTagToPriority = useAddTagToPriority()
  const createAndAssignTag = useCreateAndAssignTag()
  const removeTagFromPriority = useRemoveTagFromPriority()

  const assignedIds = new Set((assignedTags ?? []).map((t) => t.id))
  const unassignedTags = (allTags ?? []).filter((t) => !assignedIds.has(t.id))

  async function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    const label = inputValue.trim()
    if (!label) return
    // Check if a tag with this label already exists
    const existing = allTags?.find((t) => t.label.toLowerCase() === label.toLowerCase())
    if (existing) {
      if (!assignedIds.has(existing.id)) await addTagToPriority.mutateAsync({ priorityId, tagId: existing.id })
    } else {
      await createAndAssignTag.mutateAsync({ priorityId, label })
    }
    setInputValue('')
  }

  return (
    <div className="space-y-2.5">
      {/* Assigned tags */}
      {assignedTags && assignedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {assignedTags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-secondary text-secondary-foreground border border-border"
            >
              {tag.label}
              <button
                type="button"
                onClick={() => removeTagFromPriority.mutate({ priorityId, tagId: tag.id })}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label={`Remove ${tag.label}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Unassigned — shown as faint add-chips when showAll, or just the toggle */}
      {unassignedTags.length > 0 && (
        <div>
          {showAll ? (
            <div className="flex flex-wrap gap-1.5">
              {unassignedTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => addTagToPriority.mutate({ priorityId, tagId: tag.id })}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-dashed border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors"
                >
                  <Tag size={9} />
                  {tag.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowAll(false)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors px-1"
              >
                Less
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              + {unassignedTags.length} existing {unassignedTags.length === 1 ? 'tag' : 'tags'}
            </button>
          )}
        </div>
      )}

      {/* Create new tag */}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="New tag — press Enter to add"
        className="w-full text-xs bg-transparent border-b border-border/50 focus:border-border focus:outline-none py-0.5 placeholder:text-muted-foreground/50"
      />
    </div>
  )
}

// ─── Detail content (rendered when priority is defined) ────────────────────────

export function DetailContent({ priority, onClose }: { priority: Priority; onClose: () => void }) {
  const buckets = useBuckets()
  const history = usePriorityHistory(priority.id)
  const notes = useNotesByPriority(priority.id)
  const linkedEntries = useEntriesForPriority(priority.id)

  // Autosave fields (save on blur)
  const [localTitle, setLocalTitle] = useState(priority.title)
  const [localDesc, setLocalDesc] = useState(priority.description ?? '')
  const [localHours, setLocalHours] = useState(priority.hoursPerWeek?.toString() ?? '')
  const [localRisks, setLocalRisks] = useState(priority.risksIfNeglected ?? '')
  const [localStartDate, setLocalStartDate] = useState(priority.startDate ?? '')
  const [localTargetDate, setLocalTargetDate] = useState(priority.targetDate ?? '')
  const [localNextReview, setLocalNextReview] = useState(priority.nextReviewDate ?? '')

  // Sync local state when priority ID changes
  useEffect(() => {
    setLocalTitle(priority.title)
    setLocalDesc(priority.description ?? '')
    setLocalHours(priority.hoursPerWeek?.toString() ?? '')
    setLocalRisks(priority.risksIfNeglected ?? '')
    setLocalStartDate(priority.startDate ?? '')
    setLocalTargetDate(priority.targetDate ?? '')
    setLocalNextReview(priority.nextReviewDate ?? '')
  }, [priority.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const [releaseOpen, setReleaseOpen] = useState(false)
  const updatePriority = useUpdatePriority()
  const setStatus = useSetPriorityStatus()
  const archivePriority = useArchivePriority()
  const duplicatePriority = useDuplicatePriority()

  function save(data: Partial<Omit<Priority, 'id' | 'createdAt'>>) {
    updatePriority.mutate({ id: priority.id, data })
  }

  function handleTitleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') e.currentTarget.blur()
    if (e.key === 'Escape') { setLocalTitle(priority.title); e.currentTarget.blur() }
  }

  function handleStatusClick(s: PriorityStatus) {
    if (s === 'released') { setReleaseOpen(true); return }
    save({ status: s })
  }

  const userBuckets = buckets?.filter((b) => !b.isSystem) ?? []
  const systemBucket = buckets?.find((b) => b.isSystem)

  return (
    <>
      {/* Header — uses plain div so DetailContent works both inside Sheet and inline */}
      <div className="pb-0 flex-shrink-0">
        <h2 className="sr-only">{priority.title}</h2>
        <div className="flex items-start gap-2 pr-8">
          <BucketColorPicker
            color={priority.color ?? (buckets?.find((b) => b.id === priority.bucketId)?.color ?? '#6B7C8B')}
            onChange={(color) => save({ color })}
          />
          <div className="flex-1 min-w-0">
            <input
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={() => { if (localTitle.trim() && localTitle !== priority.title) save({ title: localTitle.trim() }) }}
              onKeyDown={handleTitleKeyDown}
              className="w-full text-base font-semibold bg-transparent border-b border-transparent focus:border-border focus:outline-none leading-snug"
              aria-label="Priority title"
            />
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 -mx-6 px-6 mt-4">
        <div className="space-y-5 pb-10">

          {/* Status row */}
          <div>
            <SectionLabel description="Where this priority stands right now. Move it freely as seasons shift — nothing needs to be permanent.">
              Status
            </SectionLabel>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(STATUS_CONFIG) as PriorityStatus[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleStatusClick(s)}
                  className={cn(
                    'transition-opacity',
                    priority.status !== s && 'opacity-40 hover:opacity-70',
                  )}
                  aria-pressed={priority.status === s}
                >
                  <PriorityStatusBadge status={s} />
                </button>
              ))}
            </div>
          </div>

          <Separator />

          {/* Core */}
          <div>
            <SectionLabel description="The foundation of this priority — why it belongs in your life and where it fits.">
              Core
            </SectionLabel>
            <FieldRow
              label="Rationale"
              tooltip="The 'why' behind this priority. Write it as if explaining to your future self during a hard season — it's what you'll return to when motivation fades."
            >
              <Textarea
                value={localDesc}
                onChange={(e) => setLocalDesc(e.target.value)}
                onBlur={() => { if (localDesc !== (priority.description ?? '')) save({ description: localDesc || undefined }) }}
                placeholder="Why does this matter?"
                rows={3}
                className="resize-none text-sm"
              />
            </FieldRow>
            <FieldRow
              label="Area of life"
              tooltip="Which life bucket this belongs to. Affects where it appears in your list and how it counts toward your area allocations."
            >
              <Select
                value={priority.bucketId ?? ''}
                onValueChange={(v) => save({ bucketId: v })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Unsorted" />
                </SelectTrigger>
                <SelectContent>
                  {userBuckets.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      <span className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} />
                        {b.name}
                      </span>
                    </SelectItem>
                  ))}
                  {systemBucket && (
                    <SelectItem value={systemBucket.id}>{systemBucket.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </FieldRow>
          </div>

          <Separator />

          {/* Values */}
          <div>
            <SectionLabel description="The values, principles, or Scripture this priority expresses. Create tags to build a shared vocabulary across your priorities.">
              Values
            </SectionLabel>
            <TagAssigner priorityId={priority.id} />
          </div>

          <Separator />

          {/* Character */}
          <div>
            <SectionLabel description="The nature of this priority — where it came from, what it costs you, and how long it naturally extends into the future.">
              Character
            </SectionLabel>
            <FieldRow
              label="Origin"
              tooltip="Did you actively choose this, or has it been placed on you? Both are valid — but knowing the difference changes how you hold it."
            >
              <ToggleGroup<PriorityOrigin>
                value={priority.origin}
                options={[
                  { value: 'chosen', label: 'Chosen' },
                  { value: 'obligation', label: 'Obligation' },
                ]}
                onChange={(v) => save({ origin: v })}
              />
            </FieldRow>
            <FieldRow
              label="Energy"
              tooltip="Does this priority fill you or cost you when you engage with it? Be honest, not aspirational — 'drains' doesn't mean you should drop it; it means you need to know."
            >
              <ToggleGroup<PriorityEnergy>
                value={priority.energy}
                options={[
                  { value: 'gives', label: '↑ Gives' },
                  { value: 'neutral', label: '→ Neutral' },
                  { value: 'drains', label: '↓ Drains' },
                ]}
                onChange={(v) => save({ energy: v })}
              />
            </FieldRow>
            <FieldRow
              label="Horizon"
              tooltip="How far does this priority naturally extend? Perpetual = indefinitely (faith, health); Long = years; Medium = months; Short = weeks to a few months."
            >
              <ToggleGroup<PriorityHorizon>
                value={priority.horizon}
                options={[
                  { value: 'perpetual', label: 'Perpetual' },
                  { value: 'long', label: 'Long' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'short', label: 'Short' },
                ]}
                onChange={(v) => save({ horizon: v })}
              />
            </FieldRow>
            <FieldRow
              label="Calling confidence"
              tooltip="How certain are you that this is genuinely yours to carry? 5 = deep conviction; 1 = still discerning, holding it loosely. Useful when reviewing whether to continue, hand off, or release."
            >
              <DotScale
                value={priority.callingConfidence}
                onChange={(v) => save({ callingConfidence: v })}
                aria-label="Calling confidence 1–5"
              />
            </FieldRow>
          </div>

          <Separator />

          {/* Load */}
          <div>
            <SectionLabel description="How this priority weighs on you practically and emotionally right now. Separate from time — a one-hour task can carry a five-dot load.">
              Load & wellbeing
            </SectionLabel>
            <FieldRow
              label="Load weight"
              tooltip="Cognitive and emotional heaviness, independent of hours. A brief weekly call can be 5/5 load; a 40-hour project can be 1/5. Rate what it actually feels like to carry this."
            >
              <DotScale
                value={priority.loadWeight}
                onChange={(v) => save({ loadWeight: v })}
                aria-label="Load weight 1–5"
              />
            </FieldRow>
            <FieldRow
              label="Hours / week"
              tooltip="Your honest estimate of weekly time investment — not a goal, but what it actually takes right now. Used to calculate total bandwidth load across all your priorities."
            >
              <input
                type="number"
                min={0}
                step={0.5}
                value={localHours}
                onChange={(e) => setLocalHours(e.target.value)}
                onBlur={() => {
                  const n = localHours !== '' ? parseFloat(localHours) : undefined
                  save({ hoursPerWeek: isNaN(n!) ? undefined : n })
                }}
                placeholder="—"
                className="w-20 text-sm bg-muted rounded px-2 py-1 border border-input focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </FieldRow>
            <FieldRow
              label="Stress"
              tooltip="How much stress is this priority generating right now? 5 = significant and worth naming. Noticing this pattern across your list reveals where the real pressure is coming from."
            >
              <DotScale
                value={priority.stressLevel}
                onChange={(v) => save({ stressLevel: v })}
                aria-label="Stress level 1–5"
              />
            </FieldRow>
            <FieldRow
              label="Joy"
              tooltip="How much genuine joy does engaging with this priority bring right now? 5 = life-giving, deep joy. A low-joy high-load priority isn't automatically wrong — but it's worth paying attention to."
            >
              <DotScale
                value={priority.joyLevel}
                onChange={(v) => save({ joyLevel: v })}
                aria-label="Joy level 1–5"
              />
            </FieldRow>
          </div>

          <Separator />

          {/* Time */}
          <div>
            <SectionLabel description="Cadence and dates to keep this priority from quietly drifting into neglect over time.">
              Time
            </SectionLabel>
            <FieldRow
              label="Cadence"
              tooltip="How often does this priority need intentional attention? Used in future check-ins and weekly reviews to prompt you when something has gone quiet."
            >
              <Select
                value={priority.cadence ?? 'none'}
                onValueChange={(v) => save({ cadence: v === 'none' ? undefined : (v as PriorityCadence) })}
              >
                <SelectTrigger className="h-8 text-sm w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Biweekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
            {(
              [
                ['Start date', localStartDate, setLocalStartDate, 'startDate', 'When did or will this priority begin? Optional — useful for tracking seasonal or project-based commitments.'],
                ['Target date', localTargetDate, setLocalTargetDate, 'targetDate', 'A soft horizon, not a hard deadline. When would you like to reach a natural milestone or completion point?'],
                ['Next review', localNextReview, setLocalNextReview, 'nextReviewDate', 'When do you want to revisit this priority\'s status, load, or direction? Helps prevent long stretches of passive drift.'],
              ] as const
            ).map(([label, localVal, setLocalVal, field, tooltip]) => (
              <FieldRow key={label} label={label} tooltip={tooltip}>
                <input
                  type="date"
                  value={localVal}
                  onChange={(e) => setLocalVal(e.target.value)}
                  onBlur={() => save({ [field]: localVal || undefined })}
                  className="text-sm bg-muted rounded px-2 py-1 border border-input focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </FieldRow>
            ))}
          </div>

          <Separator />

          {/* Risks */}
          <div>
            <SectionLabel description="The honest cost of consistently underinvesting here. Write it plainly — the point is to make the cost visible, not to create guilt.">
              Risks if neglected
            </SectionLabel>
            <Textarea
              value={localRisks}
              onChange={(e) => setLocalRisks(e.target.value)}
              onBlur={() => { if (localRisks !== (priority.risksIfNeglected ?? '')) save({ risksIfNeglected: localRisks || undefined }) }}
              placeholder="What happens if this gets your leftovers?"
              rows={2}
              className="resize-none text-sm"
            />
          </div>

          <Separator />

          {/* Notes */}
          <div>
            <SectionLabel description="Running observations captured in the moment — quick reactions, what surprised you, what shifted. Append-only; never edited.">
              Notes
            </SectionLabel>
            <NoteThread
              notes={notes}
              entity={{ priorityId: priority.id }}
              placeholder="Add a note about this priority…"
            />
          </div>

          <Separator />

          {/* Journal */}
          <div>
            <SectionLabel description="Journal entries linked to this priority from the journal editor.">
              Journal entries
            </SectionLabel>
            {linkedEntries && linkedEntries.length > 0 ? (
              <ul className="space-y-3">
                {linkedEntries.map((entry) => (
                  <li key={entry.id}>
                    <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wide tabular-nums">
                      {format(new Date(entry.entryDate), 'MMM d, yyyy')}
                      {' · '}
                      {entry.type === 'weekly_review' ? 'Weekly review' : entry.type === 'discernment' ? 'Discernment' : 'Free write'}
                    </p>
                    {entry.body.trim() && (
                      <p className="text-xs text-foreground/70 mt-0.5 leading-relaxed line-clamp-2">
                        {entry.body.trim().slice(0, 140)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                No linked entries. Link this priority from the journal editor.
              </p>
            )}
          </div>

          <Separator />

          {/* Privacy */}
          <div>
            <SectionLabel description="Controls whether this priority is visible to any future AI-assisted reflection or coaching features.">
              Privacy
            </SectionLabel>
            <div className="flex items-center gap-3">
              <Switch
                id="ai-exclude"
                checked={priority.aiExcludable}
                onCheckedChange={(v) => save({ aiExcludable: v })}
              />
              <Label htmlFor="ai-exclude" className="text-sm cursor-pointer">
                Exclude from AI coaching
              </Label>
            </div>
          </div>

          <Separator />

          {/* Actions */}
          <div>
            <SectionLabel>Actions</SectionLabel>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => duplicatePriority.mutate(priority.id)}
              >
                <Copy size={13} />
                Duplicate
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-destructive hover:text-destructive"
                onClick={() => { archivePriority.mutate(priority.id); onClose() }}
              >
                <Archive size={13} />
                Archive
              </Button>
            </div>
          </div>

          {/* History */}
          {history && history.length > 0 && (
            <>
              <Separator />
              <div>
                <SectionLabel description="All rank, status, and area changes for this priority, newest first.">History</SectionLabel>
                <ol className="space-y-1.5">
                  {history.map((h) => {
                    const year = new Date(h.changedAt).getFullYear()
                    const thisYear = new Date().getFullYear()
                    const dateStr = format(new Date(h.changedAt), year === thisYear ? 'MMM d' : 'MMM d, yyyy')
                    const bucketLabel = (id?: string | null) =>
                      !id ? 'Unsorted'
                      : (buckets?.find(b => b.id === id)?.name ?? 'Unknown area')
                    return (
                      <li key={h.id} className="text-xs text-muted-foreground flex gap-2">
                        <span className="text-foreground/40 flex-shrink-0 tabular-nums">{dateStr}</span>
                        <span>
                          {h.field === 'status' && `Status: ${h.oldValue ?? '—'} → ${h.newValue ?? '—'}`}
                          {h.field === 'rank' && 'Reordered within area'}
                          {h.field === 'bucketId' && `Moved: ${bucketLabel(h.oldValue)} → ${bucketLabel(h.newValue)}`}
                        </span>
                      </li>
                    )
                  })}
                </ol>
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      <ReleaseDialog
        priorityTitle={priority.title}
        open={releaseOpen}
        onOpenChange={setReleaseOpen}
        onConfirm={(note) => setStatus.mutate({ id: priority.id, status: 'released', releaseNote: note })}
      />
    </>
  )
}

// ─── Public component ─────────────────────────────────────────────────────────

interface PriorityDetailProps {
  priorityId: string | null
  onClose: () => void
}

export function PriorityDetail({ priorityId, onClose }: PriorityDetailProps) {
  const priority = usePriority(priorityId)
  const open = priorityId !== null

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[500px] flex flex-col gap-0 px-6"
      >
        {open && priority ? (
          <DetailContent priority={priority} onClose={onClose} />
        ) : open ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={onClose}><X size={16} /></Button>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
